import { useRef, useState } from 'react'
import { COMMON_OFFSET_SLIDER_MAX, DEFAULT_OFFSET } from '../constants/wrap-offsets'
import { type CommonOffset, isCommonOffsetPreset } from '../helpers/offset-display'

/** Display state of the shared preset/slider/input control that applies one value to all sides. */
export function useCommonOffsetInput() {
  const [commonSliderValue, setCommonSliderValue] = useState([DEFAULT_OFFSET])
  const [commonInputText, setCommonInputText] = useState(String(DEFAULT_OFFSET))
  const [commonSelectPreset, setCommonSelectPreset] = useState<string | null>(
    String(DEFAULT_OFFSET)
  )
  const [commonInputErrorMessage, setCommonInputErrorMessage] = useState('')
  /** True when the applicable sides across the whole selection currently disagree — the shared
   *  input shows a "Mixed" placeholder instead of a single (misleadingly specific) number in this
   *  state. Generalizes the old single-object "four sides disagree" flag to also cover "objects
   *  disagree with each other" — see `aggregateCommonOffset`. */
  const [commonValuesMixed, setCommonValuesMixed] = useState(false)
  const commonInputFocused = useRef(false)
  const commonLastValidInputRef = useRef(String(DEFAULT_OFFSET))

  /** No eligible target: back to the 6pt default (preset highlighted), no error. */
  const reset = () => {
    setCommonSliderValue([DEFAULT_OFFSET])
    setCommonInputText(String(DEFAULT_OFFSET))
    setCommonInputErrorMessage('')
    setCommonValuesMixed(false)
    commonLastValidInputRef.current = String(DEFAULT_OFFSET)
    setCommonSelectPreset(String(DEFAULT_OFFSET))
  }

  /** Shows the selection's aggregated common value, or "Mixed" when it disagrees. */
  const showAggregate = ({ value, allSame }: CommonOffset) => {
    setCommonSliderValue([Math.min(COMMON_OFFSET_SLIDER_MAX, Math.max(0, value))])
    setCommonInputText(allSame ? String(value) : '')
    setCommonValuesMixed(!allSame)
    commonLastValidInputRef.current = allSame ? String(value) : commonLastValidInputRef.current
    setCommonInputErrorMessage('')
    setCommonSelectPreset(allSame && isCommonOffsetPreset(value) ? String(value) : null)
  }

  /** Shows a value the shared control just committed to every side. */
  const showCommitted = (value: number) => {
    setCommonSliderValue([Math.min(COMMON_OFFSET_SLIDER_MAX, value)])
    setCommonInputText(String(value))
    commonLastValidInputRef.current = String(value)
    setCommonInputErrorMessage('')
    setCommonValuesMixed(false)
    setCommonSelectPreset(isCommonOffsetPreset(value) ? String(value) : null)
  }

  /** Mirrors a slider drag tick into the input and preset highlight (no commit). */
  const previewSlider = (value: number) => {
    setCommonSliderValue([value])
    setCommonInputText(String(value))
    setCommonSelectPreset(isCommonOffsetPreset(value) ? String(value) : null)
  }

  /** Always show exactly what was typed — letters included — with its validation message. */
  const showTyped = (raw: string, message: string) => {
    setCommonInputText(raw)
    setCommonInputErrorMessage(message)
  }

  /** Restores the last valid text and clears the error. */
  const revert = () => {
    setCommonInputText(commonLastValidInputRef.current)
    setCommonInputErrorMessage('')
  }

  const clearError = () => {
    setCommonInputErrorMessage('')
  }

  return {
    commonSliderValue,
    commonInputText,
    commonSelectPreset,
    commonInputErrorMessage,
    commonValuesMixed,
    commonInputFocused,
    reset,
    showAggregate,
    showCommitted,
    previewSlider,
    showTyped,
    revert,
    clearError,
  }
}
